import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { resetTicketUnreadCount } from 'src/utils/ticketUnread';
import { useToaster } from 'src/utils/toaster/toasterContext';

const PAGE_SIZE = 20;

const oneLineEllipsisSx = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const getQueryNumber = (key) => {
  if (typeof window === 'undefined') return 0;
  return Number(new URLSearchParams(window.location.search).get(key) || 0);
};

const formatDate = (value) => {
  if (!value) return '';
  return CommonHelper.formatDateAndTime(value) || '';
};

const formatTime = (value) => {
  if (!value) return '';
  return CommonHelper.formatDateAndTime(value, 'time') || '';
};

const safeText = (value, fallback = '') => String(value || fallback).trim();

const requesterName = (ticket) => safeText(ticket?.requester_name) || safeText(ticket?.requester_email, 'Visitor');

const avatarInitial = (value) => safeText(value, 'V').slice(0, 1).toUpperCase();

const getCountryCode = (value) => {
  const code = safeText(value)
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 2);
  return /^[A-Z]{2}$/.test(code) ? code : '';
};

const getTicketCountryCode = (ticket) => {
  const candidates = [ticket?.country_flag, ticket?.requester_country, ticket?.visitor_country, ticket?.country];
  for (const value of candidates) {
    const code = getCountryCode(value);
    if (code) return code;
  }
  return '';
};

const TicketRequesterAvatar = ({ ticket, name, size = 40, sx = {} }) => {
  const countryCode = getTicketCountryCode(ticket);
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: countryCode ? '#fff' : 'primary.main',
        color: countryCode ? 'text.secondary' : 'primary.contrastText',
        fontWeight: 900,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {countryCode ? (
        <ReactCountryFlag
          countryCode={countryCode}
          svg
          style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }}
          title={countryCode}
        />
      ) : (
        avatarInitial(name)
      )}
    </Avatar>
  );
};

const displayTicketNo = (ticket) => {
  if (!ticket) return '#';
  const rawNo = safeText(ticket.ticket_no);
  if (rawNo && !rawNo.startsWith('TMP-')) {
    const sequenceMatch = rawNo.match(/(\d+)$/);
    if (sequenceMatch) return `#${Number(sequenceMatch[1])}`;
    return rawNo;
  }
  if (ticket.id) return `#${ticket.id}`;
  return '#';
};

const threadTicketNo = (ticket) => {
  const rawNo = safeText(ticket?.ticket_no);
  return rawNo && !rawNo.startsWith('TMP-') ? rawNo : '';
};

const firstPositiveNumber = (...values) => {
  for (const value of values) {
    const number = Number(value || 0);
    if (Number.isFinite(number) && number > 0) return number;
  }
  return 0;
};

const getTicketConversationID = (ticket) =>
  firstPositiveNumber(ticket?.conversation_id, ticket?.conversationId, ticket?.conversationID);

const getTicketMessageCount = (ticket, items) => {
  if (Array.isArray(items) && items.length > 0) return items.length;
  return firstPositiveNumber(
    ticket?.message_count,
    ticket?.messageCount,
    ticket?.messages_count,
    ticket?.messagesCount
  );
};

const cleanMessageContent = (value) => {
  const text = safeText(value);
  if (!text) return '';
  const withoutQuotedThread = text.split(/\nOn\s.+?wrote:\s*\n/i)[0];
  const withoutQuotedLines = withoutQuotedThread
    .split('\n')
    .filter((line) => !line.trim().startsWith('>'))
    .join('\n');
  return safeText(withoutQuotedLines) || text;
};

const htmlToReadableText = (value) => {
  const html = safeText(value);
  if (!html) return '';
  const withLineBreaks = html
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/\s*(p|div|tr|li|table|section|article|h[1-6])\s*>/gi, '\n');
  const withoutScripts = withLineBreaks
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '');
  const withoutTags = withoutScripts.replace(/<[^>]+>/g, ' ');
  const decoded = withoutTags
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
  return decoded
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
};

const readableMailboxMessage = (message) => {
  const body = safeText(message?.message);
  if (/^\s*</.test(body) || /<\/?[a-z][\s\S]*>/i.test(body)) {
    return cleanMessageContent(htmlToReadableText(body));
  }
  return cleanMessageContent(body);
};

const readableTicketMessage = (message) => {
  const htmlBody = safeText(message?.html_message || message?.htmlMessage);
  if (htmlBody) {
    return cleanMessageContent(htmlToReadableText(htmlBody));
  }
  return readableMailboxMessage(message);
};

const isInternalMailboxAddress = (value) => {
  const email = safeText(value).toLowerCase();
  return email.endsWith('@onlinedevtool.org') && email.startsWith('ticket-');
};

const statusColor = (status) => (status === 'open' ? 'success' : 'default');

const mergeUniqueById = (currentItems, nextItems, nextFirst = false) => {
  const seen = new Set();
  const orderedItems = nextFirst ? [...nextItems, ...currentItems] : [...currentItems, ...nextItems];
  return orderedItems.filter((item) => {
    const id = item?.id;
    if (id === undefined || id === null) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const parseCCList = (value) =>
  safeText(value)
    .split(/[;,\s]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const formatFileSize = (bytes) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileToAttachment = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve({
        name: file.name,
        content_type: file.type || 'application/octet-stream',
        content_base64: result.includes(',') ? result.split(',').pop() : result,
        size: file.size,
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function TicketsPage() {
  const theme = useTheme();
  const isMobileTicketView = useMediaQuery(theme.breakpoints.down('lg'));
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState('open');
  const [searchText, setSearchText] = useState('');
  const [loader, setLoader] = useState(false);
  const [detailLoader, setDetailLoader] = useState(false);
  const [reply, setReply] = useState('');
  const [ccOpen, setCcOpen] = useState(false);
  const [ccValue, setCcValue] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [statusLoader, setStatusLoader] = useState(false);
  const [statusConfirmAction, setStatusConfirmAction] = useState(null);
  const [mailbox, setMailbox] = useState(null);
  const [ticketPage, setTicketPage] = useState(1);
  const [loadingMoreTickets, setLoadingMoreTickets] = useState(false);
  const [manualTicketOpen, setManualTicketOpen] = useState(false);
  const [manualTicketSaving, setManualTicketSaving] = useState(false);
  const [ticketSetupDone, setTicketSetupDone] = useState(false);
  const [manualTicket, setManualTicket] = useState({
    requester_name: '',
    requester_email: '',
    subject: '',
    message: '',
  });
  const selectedTicketRef = useRef(null);
  const ticketDetailRequestRef = useRef(0);
  const ticketsLoaderRef = useRef(false);
  const hasMoreTicketsRef = useRef(false);

  const selectedId = selectedTicket?.id || 0;

  const applySelectedTicket = useCallback((ticket) => {
    selectedTicketRef.current = ticket || null;
    setSelectedTicket(ticket || null);
  }, []);

  const fetchMailbox = useCallback(() => {
    HttpServices.Get({
      url: userModuleURL.ticketMailbox,
      successCallback: (data) => setMailbox(data || null),
      failureCallback: () => {},
    });
  }, []);

  const fetchTicketSetupStatus = useCallback(() => {
    HttpServices.Get({
      url: userModuleURL.getChatSetupStatus,
      successCallback: (data) => {
        const ticketStep = (data?.steps || []).find((step) => step?.key === 'ticket_smtp_configured');
        setTicketSetupDone(Boolean(ticketStep?.is_complete));
      },
      failureCallback: () => setTicketSetupDone(false),
    });
  }, []);

  const fetchTickets = useCallback(
    (nextStatus = 'open', options = {}) => {
      const page = Number(options.page || 1);
      const append = Boolean(options.append);
      const preserveExisting = Boolean(options.preserveExisting);
      const silent = Boolean(options.silent);
      if (append && (ticketsLoaderRef.current || !hasMoreTicketsRef.current)) return;
      ticketsLoaderRef.current = true;
      if (append) setLoadingMoreTickets(true);
      else if (!silent) setLoader(true);
      const targetTicketId = Number(options.targetTicketId || 0);
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (nextStatus && nextStatus !== 'all') params.set('status', nextStatus);
      HttpServices.Get({
        url: `${userModuleURL.tickets}?${params.toString()}`,
        successCallback: (data) => {
          const nextTickets = data?.tickets || [];
          const nextPage = Number(data?.page || page);
          const nextHasMore = data?.has_more !== undefined ? Boolean(data.has_more) : nextTickets.length >= PAGE_SIZE;
          setTicketPage(nextPage);
          hasMoreTicketsRef.current = nextHasMore;
          const current = selectedTicketRef.current;
          const targetTicket = targetTicketId
            ? nextTickets.find((ticket) => Number(ticket.id) === targetTicketId)
            : null;
          let nextSelectedTicket = current;
          if (!append) {
            nextSelectedTicket = targetTicket || null;
            if (!nextSelectedTicket) {
              nextSelectedTicket =
                targetTicketId && Number(current?.id || 0) === targetTicketId
                  ? current
                  : current && nextTickets.some((ticket) => ticket.id === current.id)
                  ? current
                  : nextTickets[0] || null;
            }
          } else if (targetTicket) {
            nextSelectedTicket = targetTicket;
          }
          setTickets((currentTickets) => {
            if (append) return mergeUniqueById(currentTickets, nextTickets);
            if (preserveExisting) return mergeUniqueById(currentTickets, nextTickets, true);
            return nextTickets;
          });
          if ((current?.id || 0) !== (nextSelectedTicket?.id || 0)) {
            ticketDetailRequestRef.current += 1;
            setMessages([]);
            setDetailLoader(false);
          }
          applySelectedTicket(nextSelectedTicket);
          ticketsLoaderRef.current = false;
          setLoadingMoreTickets(false);
          setLoader(false);
        },
        failureCallback: (message) => {
          ticketsLoaderRef.current = false;
          setLoadingMoreTickets(false);
          setLoader(false);
          showToast(message || 'Failed to load tickets', 'e');
        },
      });
    },
    [applySelectedTicket, showToast]
  );

  const fetchTicketDetails = useCallback(
    (ticketId, options = {}) => {
      if (!ticketId) return;
      const allowUnselected = Boolean(options.allowUnselected);
      const requestId = ticketDetailRequestRef.current + 1;
      ticketDetailRequestRef.current = requestId;
      setDetailLoader(true);
      HttpServices.Get({
        url: `${userModuleURL.tickets}/${ticketId}`,
        successCallback: (data) => {
          if (requestId !== ticketDetailRequestRef.current) return;
          if (!allowUnselected && selectedTicketRef.current?.id !== ticketId) return;
          const nextTicket = data?.ticket || null;
          applySelectedTicket(nextTicket);
          if (nextTicket?.id) {
            setTickets((current) => {
              const exists = current.some((ticket) => ticket.id === nextTicket.id);
              if (!exists) return [{ ...nextTicket, unread_count: 0 }, ...current];
              return current.map((ticket) =>
                ticket.id === nextTicket.id ? { ...ticket, ...nextTicket, unread_count: 0 } : ticket
              );
            });
          }
          setMessages(data?.messages || []);
          setDetailLoader(false);
        },
        failureCallback: (message) => {
          if (requestId !== ticketDetailRequestRef.current) return;
          setDetailLoader(false);
          showToast(message || 'Failed to load ticket', 'e');
        },
      });
    },
    [applySelectedTicket, showToast]
  );

  useEffect(() => {
    const targetTicketId = getQueryNumber('ticket_id');
    if (targetTicketId) {
      applySelectedTicket({ id: targetTicketId });
      fetchTicketDetails(targetTicketId, { allowUnselected: true });
    }
    fetchMailbox();
    fetchTicketSetupStatus();
    fetchTickets('open', { targetTicketId });
  }, [applySelectedTicket, fetchMailbox, fetchTicketDetails, fetchTicketSetupStatus, fetchTickets]);

  useEffect(() => {
    resetTicketUnreadCount();
    const handleTicketsRefresh = (event) => {
      const type = event?.detail?.type || '';
      fetchMailbox();
      fetchTicketSetupStatus();
      fetchTickets(status);
      if (type !== 'ticket:new' && selectedTicketRef.current?.id) {
        fetchTicketDetails(selectedTicketRef.current.id);
      }
    };

    window.addEventListener('brand2chat:tickets-refresh', handleTicketsRefresh);
    window.addEventListener('brand2chat:setup-status-refresh', fetchTicketSetupStatus);
    return () => {
      window.removeEventListener('brand2chat:tickets-refresh', handleTicketsRefresh);
      window.removeEventListener('brand2chat:setup-status-refresh', fetchTicketSetupStatus);
    };
  }, [fetchMailbox, fetchTicketDetails, fetchTicketSetupStatus, fetchTickets, status]);

  useEffect(() => {
    if (selectedId) fetchTicketDetails(selectedId);
  }, [selectedId, fetchTicketDetails]);

  const filteredTickets = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return tickets;
    return tickets.filter((ticket) =>
      [
        displayTicketNo(ticket),
        threadTicketNo(ticket),
        ticket.subject,
        ticket.requester_name,
        ticket.requester_email,
        ticket.last_message,
      ].some((value) => safeText(value).toLowerCase().includes(query))
    );
  }, [searchText, tickets]);

  const selectedConversationID = getTicketConversationID(selectedTicket);
  const messageCount = getTicketMessageCount(selectedTicket, messages);

  const inboxCount = filteredTickets.length;
  const inboxCountLabel = translate(
    inboxCount === 1 ? 'tickets_page.ticket_count_one' : 'tickets_page.ticket_count_many',
    {
      count: inboxCount,
      defaultValue: inboxCount === 1 ? '{{count}} ticket' : '{{count}} tickets',
    }
  );
  const translateTicketStatus = (value) => {
    const normalized = safeText(value, 'open').toLowerCase();
    return translate(`tickets_page.${normalized}`, { defaultValue: value || '-' });
  };
  const attachmentReadyLabel = attachments.length
    ? translate(attachments.length === 1 ? 'tickets_page.attachment_ready_one' : 'tickets_page.attachment_ready_many', {
        count: attachments.length,
        defaultValue: attachments.length === 1 ? '{{count}} attachment ready' : '{{count}} attachments ready',
      })
    : translate('tickets_page.add_files_before_sending', { defaultValue: 'Add files before sending if needed' });

  const handleStatusChange = (_event, value) => {
    if (!value) return;
    setStatus(value);
    ticketDetailRequestRef.current += 1;
    applySelectedTicket(null);
    setMessages([]);
    setDetailLoader(false);
    fetchTickets(value);
  };

  const handleInboxListScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollTop + clientHeight < scrollHeight - 120) return;
    fetchTickets(status, { page: ticketPage + 1, append: true, silent: true });
  };

  const handleTicketSelect = (ticket) => {
    if (!ticket) return;
    resetTicketUnreadCount();
    if ((selectedTicketRef.current?.id || 0) !== ticket.id) {
      ticketDetailRequestRef.current += 1;
      setMessages([]);
    }
    applySelectedTicket(ticket);
  };
  const URL_REGEX = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

  const linkifyText = (text) => {
    const content = safeText(text);
    if (!content) return null;

    const parts = [];
    let lastIndex = 0;
    let match;
    const regex = new RegExp(URL_REGEX.source, URL_REGEX.flags);

    while ((match = regex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index));
      }

      let url = match[0];
      // Drop trailing punctuation that often sticks to pasted links in email
      const trailingMatch = url.match(/[),.;:!?]+$/);
      let suffix = '';
      if (trailingMatch) {
        suffix = trailingMatch[0];
        url = url.slice(0, -suffix.length);
      }

      const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;

      parts.push(
        <Box
          key={`link-${match.index}`}
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: 'inherit',
            textDecoration: 'underline',
            fontWeight: 600,
            wordBreak: 'break-all',
            '&:hover': { opacity: 0.85 },
          }}
        >
          {url}
        </Box>
      );

      if (suffix) parts.push(suffix);
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return parts.length ? parts : content;
  };
  const sendReply = () => {
    const message = reply.trim();
    if (!selectedId || !message) return;
    setSending(true);
    HttpServices.Post({
      url: `${userModuleURL.tickets}/${selectedId}/reply`,
      body: { message, cc: parseCCList(ccValue), attachments },
      successCallback: () => {
        setReply('');
        setCcValue('');
        setCcOpen(false);
        setAttachments([]);
        setSending(false);
        fetchTicketDetails(selectedId);
        fetchTickets(status);
        showToast(translate('tickets_page.reply_sent', { defaultValue: 'Reply sent' }), 's');
      },
      failureCallback: (messageText) => {
        setSending(false);
        showToast(
          messageText || translate('tickets_page.failed_send_reply', { defaultValue: 'Failed to send reply' }),
          'e'
        );
      },
    });
  };

  const handleAttachmentChange = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    Promise.all(files.map(fileToAttachment))
      .then((nextAttachments) => {
        setAttachments((current) => [...current, ...nextAttachments].slice(0, 5));
      })
      .catch(() =>
        showToast(translate('tickets_page.failed_read_attachment', { defaultValue: 'Failed to read attachment' }), 'e')
      );
  };

  const removeAttachment = (index) => {
    setAttachments((current) => current.filter((_item, itemIndex) => itemIndex !== index));
  };

  const updateStatus = (nextAction) => {
    if (!selectedId) return;
    setStatusLoader(true);
    HttpServices.Post({
      url: `${userModuleURL.tickets}/${selectedId}/${nextAction}`,
      body: {},
      successCallback: () => {
        if (status !== 'all') {
          ticketDetailRequestRef.current += 1;
          applySelectedTicket(null);
          setMessages([]);
          setDetailLoader(false);
        } else {
          fetchTicketDetails(selectedId);
        }
        fetchTickets(status);
        setStatusLoader(false);
        setStatusConfirmAction(null);
      },
      failureCallback: (messageText) => {
        setStatusLoader(false);
        setStatusConfirmAction(null);
        showToast(
          messageText || translate('tickets_page.failed_update_ticket', { defaultValue: 'Failed to update ticket' }),
          'e'
        );
      },
    });
  };

  const confirmTicketStatusChange = () => {
    if (!statusConfirmAction) return;
    updateStatus(statusConfirmAction);
  };

  const updateManualTicket = (field, value) => {
    setManualTicket((current) => ({ ...current, [field]: value }));
  };

  const resetManualTicket = () => {
    setManualTicket({
      requester_name: '',
      requester_email: '',
      subject: '',
      message: '',
    });
  };

  const closeManualTicketDialog = () => {
    if (manualTicketSaving) return;
    setManualTicketOpen(false);
  };

  const createManualTicket = () => {
    const payload = {
      requester_name: manualTicket.requester_name.trim(),
      requester_email: manualTicket.requester_email.trim(),
      subject: manualTicket.subject.trim(),
      message: manualTicket.message.trim(),
    };
    if (!payload.requester_email || !payload.subject || !payload.message) {
      showToast(
        translate('tickets_page.manual_ticket_required', {
          defaultValue: 'Requester email, subject, and message are required',
        }),
        'e'
      );
      return;
    }
    setManualTicketSaving(true);
    HttpServices.Post({
      url: userModuleURL.tickets,
      body: payload,
      successCallback: (data) => {
        setManualTicketSaving(false);
        setManualTicketOpen(false);
        resetManualTicket();
        const ticket = data || {};
        if (ticket?.id) {
          ticketDetailRequestRef.current += 1;
          setMessages([]);
          setStatus('open');
          applySelectedTicket(ticket);
          setTickets((current) => mergeUniqueById([ticket], current, true));
          fetchTicketDetails(ticket.id, { allowUnselected: true });
          fetchTickets('open', { targetTicketId: ticket.id, preserveExisting: true, silent: true });
        } else {
          fetchTickets('open');
        }
        showToast(translate('tickets_page.manual_ticket_created', { defaultValue: 'Ticket created' }), 's');
      },
      failureCallback: (messageText) => {
        setManualTicketSaving(false);
        showToast(
          messageText || translate('tickets_page.manual_ticket_failed', { defaultValue: 'Failed to create ticket' }),
          'e'
        );
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>{translate('tickets_page.title', { defaultValue: 'Tickets' })}</title>
      </Helmet>

      {isMobileTicketView ? (
        <MobileTicketsView
          translate={translate}
          mailbox={mailbox}
          status={status}
          handleStatusChange={handleStatusChange}
          searchText={searchText}
          setSearchText={setSearchText}
          fetchTickets={fetchTickets}
          filteredTickets={filteredTickets}
          loader={loader}
          loadingMoreTickets={loadingMoreTickets}
          handleInboxListScroll={handleInboxListScroll}
          handleTicketSelect={handleTicketSelect}
          inboxCountLabel={inboxCountLabel}
          inboxCount={inboxCount}
          selectedTicket={selectedTicket}
          selectedId={selectedId}
          messages={messages}
          detailLoader={detailLoader}
          translateTicketStatus={translateTicketStatus}
          statusLoader={statusLoader}
          setStatusConfirmAction={setStatusConfirmAction}
          reply={reply}
          setReply={setReply}
          ccOpen={ccOpen}
          setCcOpen={setCcOpen}
          ccValue={ccValue}
          setCcValue={setCcValue}
          attachments={attachments}
          handleAttachmentChange={handleAttachmentChange}
          removeAttachment={removeAttachment}
          attachmentReadyLabel={attachmentReadyLabel}
          sending={sending}
          sendReply={sendReply}
          linkifyText={linkifyText}
          ticketSetupDone={ticketSetupDone}
          openCreateTicketDialog={() => setManualTicketOpen(true)}
          clearSelectedTicket={() => {
            applySelectedTicket(null);
            setMessages([]);
          }}
        />
      ) : (
      <Box
        sx={{
          height: { xs: '100%', lg: 'calc(100vh - 108px)' },
          minHeight: { xs: 0, lg: 680 },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1, lg: 1.5 },
          p: { xs: 1, sm: 1.5, lg: 0 },
          overflow: 'hidden',
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 1.25,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(
              theme.palette.background.paper,
              0.96
            )} 46%, ${alpha(theme.palette.success.main, 0.08)})`,
          }}
        >
          <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.main', width: 30, height: 30 }}>
                  <ConfirmationNumberIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
                    {translate('tickets_page.title', { defaultValue: 'Tickets' })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {translate('tickets_page.subtitle', { defaultValue: 'Missed chats and email replies.' })}
                  </Typography>
                </Box>
              </Stack>
              {mailbox?.email_address && !isInternalMailboxAddress(mailbox.email_address) && (
                <Chip
                  icon={<MailOutlineIcon />}
                  label={mailbox.email_address}
                  size="small"
                  sx={{ mt: 0.75, height: 24, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                />
              )}
            </Box>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', lg: 'flex-end' }} alignItems="center">
              {ticketSetupDone && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setManualTicketOpen(true)}
                  sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}
                >
                  {translate('tickets_page.create_ticket', { defaultValue: 'Create Ticket' })}
                </Button>
              )}
              <Tooltip title={translate('tickets_page.refresh_tickets', { defaultValue: 'Refresh tickets' })}>
                <IconButton onClick={() => fetchTickets(status)}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ flex: 1, minHeight: 0, borderRadius: 3, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '330px minmax(420px, 1fr) 330px' },
              height: '100%',
            }}
          >
            <Box
              sx={{
                borderRight: { lg: '1px solid' },
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.primary.main, 0.025),
                minHeight: 0,
                display: { xs: selectedTicket ? 'none' : 'block', lg: 'block' },
              }}
            >
              {loader && <LinearProgress />}
              <Stack spacing={1.5} sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" fontWeight={800}>
                    {translate('tickets_page.inbox', { defaultValue: 'Inbox' })}
                  </Typography>
                  <Chip label={inboxCountLabel} size="small" />
                </Stack>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={status}
                  onChange={handleStatusChange}
                  sx={{
                    '& .MuiToggleButton-root': {
                      minWidth: 0,
                      px: 1,
                      py: 0.75,
                      lineHeight: 1,
                      textTransform: 'none',
                      fontWeight: 700,
                    },
                  }}
                >
                  <ToggleButton value="all">{translate('tickets_page.all', { defaultValue: 'All' })}</ToggleButton>
                  <ToggleButton value="open">{translate('tickets_page.open', { defaultValue: 'Open' })}</ToggleButton>
                  <ToggleButton value="closed">
                    {translate('tickets_page.closed', { defaultValue: 'Closed' })}
                  </ToggleButton>
                </ToggleButtonGroup>
                <TextField
                  size="small"
                  fullWidth
                  placeholder={translate('tickets_page.search_tickets', { defaultValue: 'Search tickets...' })}
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
              <Divider />
              <List
                disablePadding
                onScroll={handleInboxListScroll}
                sx={{ overflowY: 'auto', overflowX: 'hidden', height: 'calc(100% - 205px)' }}
              >
                {filteredTickets.map((ticket) => {
                  const active = ticket.id === selectedId;
                  const name = requesterName(ticket);
                  const rowMessageCount = getTicketMessageCount(ticket);
                  const rowUnreadCount = Number(ticket.unread_count || 0);
                  return (
                    <ListItemButton
                      key={ticket.id}
                      selected={active}
                      onClick={() => handleTicketSelect(ticket)}
                      sx={{
                        alignItems: 'flex-start',
                        gap: 1.25,
                        py: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        borderLeft: '4px solid',
                        borderLeftColor: active ? 'primary.main' : 'transparent',
                        width: '100%',
                        minWidth: 0,
                        overflow: 'hidden',
                      }}
                    >
                      <Badge
                        color="warning"
                        variant="dot"
                        invisible={Number(ticket.unread_count || 0) <= 0}
                        overlap="circular"
                      >
                        <TicketRequesterAvatar
                          ticket={ticket}
                          name={name}
                          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15), color: 'primary.dark' }}
                        />
                      </Badge>
                      <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" fontWeight={800} sx={oneLineEllipsisSx}>
                            {name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {formatTime(ticket.updated_at)}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25, minWidth: 0 }}>
                          <Typography variant="body2" color="primary.main" fontWeight={800} sx={{ flexShrink: 0 }}>
                            {displayTicketNo(ticket)}
                          </Typography>
                          <Typography variant="body2" sx={{ ...oneLineEllipsisSx, flex: 1 }}>
                            {ticket.subject ||
                              translate('tickets_page.support_ticket', { defaultValue: 'Support ticket' })}
                          </Typography>
                          {rowMessageCount > 0 && (
                            <Chip
                              icon={<ChatBubbleOutlineIcon />}
                              label={rowMessageCount > 99 ? '99+' : rowMessageCount}
                              size="small"
                              color={rowUnreadCount > 0 ? 'error' : 'default'}
                              variant={rowUnreadCount > 0 ? 'filled' : 'outlined'}
                              sx={{
                                height: 20,
                                flexShrink: 0,
                                fontWeight: 800,
                                '& .MuiChip-icon': { fontSize: 14, ml: 0.6, mr: -0.2 },
                                '& .MuiChip-label': { px: 0.7, fontSize: 11 },
                              }}
                            />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ ...oneLineEllipsisSx, display: 'block', maxWidth: '100%' }}>
                          {ticket.requester_email || translate('tickets_page.no_email', { defaultValue: 'No email' })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ...oneLineEllipsisSx, display: 'block', maxWidth: '100%', mt: 0.35 }}>
                          {cleanMessageContent(ticket.last_message) ||
                            translate('tickets_page.no_message_preview', { defaultValue: 'No message preview' })}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  );
                })}
                {loadingMoreTickets && (
                  <Stack alignItems="center" sx={{ py: 1.5 }}>
                    <CircularProgress size={18} />
                  </Stack>
                )}
                {!loader && !inboxCount && (
                  <Stack alignItems="center" spacing={1} sx={{ py: 8, px: 2 }}>
                    <ConfirmationNumberIcon color="disabled" />
                    <Typography variant="body2" color="text.secondary">
                      {translate('tickets_page.no_tickets_found', { defaultValue: 'No tickets found.' })}
                    </Typography>
                  </Stack>
                )}
              </List>
            </Box>

            <Box
              sx={{
                display: { xs: selectedTicket ? 'flex' : 'none', lg: 'flex' },
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              {detailLoader && <LinearProgress />}
              {selectedTicket ? (
                <>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <IconButton
                          size="small"
                          onClick={() => {
                            applySelectedTicket(null);
                            setMessages([]);
                          }}
                          sx={{ display: { xs: 'inline-flex', lg: 'none' } }}
                        >
                          <ArrowBackIcon fontSize="small" />
                        </IconButton>
                        <Chip
                          label={displayTicketNo(selectedTicket)}
                          color="primary"
                          size="small"
                          sx={{ fontWeight: 800 }}
                        />
                        {threadTicketNo(selectedTicket) && (
                          <Chip label={threadTicketNo(selectedTicket)} size="small" variant="outlined" />
                        )}
                        <Chip
                          label={translateTicketStatus(selectedTicket.status)}
                          color={statusColor(selectedTicket.status)}
                          size="small"
                        />
                      </Stack>
                      <Typography variant="h6" fontWeight={850} noWrap sx={{ mt: 0.75 }}>
                        {selectedTicket.subject ||
                          translate('tickets_page.support_ticket', { defaultValue: 'Support ticket' })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {requesterName(selectedTicket)} &lt;
                        {selectedTicket.requester_email ||
                          translate('tickets_page.no_email', { defaultValue: 'No email' })}
                        &gt;
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {selectedTicket.status === 'open' ? (
                        <Button
                          variant="outlined"
                          color="inherit"
                          startIcon={<CloseIcon />}
                          disabled={statusLoader}
                          onClick={() => setStatusConfirmAction('close')}
                        >
                          {translate('tickets_page.close', { defaultValue: 'Close' })}
                        </Button>
                      ) : (
                        <Button variant="contained" disabled={statusLoader} onClick={() => setStatusConfirmAction('reopen')}>
                          {translate('tickets_page.reopen', { defaultValue: 'Reopen' })}
                        </Button>
                      )}
                    </Stack>
                  </Stack>

                  <Stack
                    spacing={1.15}
                    sx={{ p: 1.25, flex: 1, overflow: 'auto', bgcolor: alpha(theme.palette.grey[500], 0.035) }}
                  >
                    {messages.map((message) => {
                      const isAgent = message.sender_type === 'agent';
                      const author =
                        safeText(message.sender_name) || safeText(message.sender_email) || message.sender_type;
                      const body = readableTicketMessage(message);
                      return (
                        <Box
                          key={message.id}
                          sx={{ display: 'flex', justifyContent: isAgent ? 'flex-end' : 'flex-start' }}
                        >
                          <Stack
                            direction={isAgent ? 'row-reverse' : 'row'}
                            spacing={1}
                            alignItems="flex-start"
                            sx={{ maxWidth: '86%' }}
                          >
                            {isAgent ? (
                              <Avatar
                                sx={{
                                  width: 30,
                                  height: 30,
                                  bgcolor: 'primary.main',
                                  color: 'primary.contrastText',
                                  fontWeight: 800,
                                }}
                              >
                                {avatarInitial(author)}
                              </Avatar>
                            ) : (
                              <TicketRequesterAvatar
                                ticket={selectedTicket}
                                name={author}
                                size={30}
                                sx={{ bgcolor: alpha(theme.palette.success.main, 0.16), color: 'success.dark' }}
                              />
                            )}
                            <Box>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                justifyContent={isAgent ? 'flex-end' : 'flex-start'}
                              >
                                <Typography variant="subtitle2" fontWeight={800}>
                                  {author}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(message.created_at)}
                                </Typography>
                              </Stack>
                              <Box
                                sx={{
                                  mt: 0.35,
                                  p: 1.15,
                                  borderRadius: 2.5,
                                  bgcolor: isAgent ? 'primary.main' : 'background.paper',
                                  color: isAgent ? 'primary.contrastText' : 'text.primary',
                                  border: isAgent ? 'none' : '1px solid',
                                  borderColor: 'divider',
                                  boxShadow: isAgent ? `0 14px 30px ${alpha(theme.palette.primary.main, 0.2)}` : 'none',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.7,
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {body
                                    ? linkifyText(body)
                                    : translate('tickets_page.message_received', {
                                        defaultValue: 'Message received.',
                                      })}
                                </Typography>
                                {message.attachments?.length > 0 && (
                                  <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
                                    {message.attachments.map((attachment) => (
                                      <Chip
                                        key={attachment.id || attachment.url}
                                        component="a"
                                        href={attachment.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        clickable
                                        icon={<AttachFileIcon />}
                                        label={`${
                                          attachment.name ||
                                          translate('tickets_page.attachment', { defaultValue: 'Attachment' })
                                        }${attachment.size ? ` (${formatFileSize(attachment.size)})` : ''}`}
                                        size="small"
                                        sx={{
                                          bgcolor: isAgent
                                            ? alpha(theme.palette.common.white, 0.16)
                                            : alpha(theme.palette.primary.main, 0.08),
                                          color: isAgent ? 'primary.contrastText' : 'text.primary',
                                          '& .MuiChip-icon': {
                                            color: isAgent ? 'primary.contrastText' : 'primary.main',
                                          },
                                        }}
                                      />
                                    ))}
                                  </Stack>
                                )}
                              </Box>
                            </Box>
                          </Stack>
                        </Box>
                      );
                    })}
                    {!messages.length && (
                      <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ minHeight: 260 }}>
                        <MailOutlineIcon color="disabled" />
                        <Typography color="text.secondary">
                          {translate('tickets_page.no_messages_yet', { defaultValue: 'No messages yet.' })}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  <Box sx={{ p: 1.25, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: 2.5,
                        overflow: 'hidden',
                        bgcolor: alpha(theme.palette.background.paper, 0.98),
                      }}
                    >
                      <Stack spacing={0.5} sx={{ px: 1.5, py: 0.85 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 28 }}>
                            {translate('tickets_page.to', { defaultValue: 'To' })}:
                          </Typography>
                          <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1 }}>
                            {selectedTicket.requester_email ||
                              translate('tickets_page.no_email', { defaultValue: 'No email' })}
                          </Typography>
                          <Button
                            size="small"
                            variant={ccOpen ? 'contained' : 'outlined'}
                            onClick={() => setCcOpen((value) => !value)}
                            sx={{ borderRadius: 5 }}
                          >
                            CC
                          </Button>
                        </Stack>
                        {ccOpen && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 28 }}>
                              {translate('tickets_page.cc_label', { defaultValue: 'Cc' })}:
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              value={ccValue}
                              onChange={(event) => setCcValue(event.target.value)}
                              placeholder={translate('tickets_page.add_cc_placeholder', {
                                defaultValue: 'Add CC emails separated by comma',
                              })}
                              variant="standard"
                              InputProps={{ disableUnderline: true }}
                            />
                          </Stack>
                        )}
                      </Stack>
                      <Divider />
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={6}
                        value={reply}
                        onChange={(event) => setReply(event.target.value)}
                        placeholder={translate('tickets_page.write_reply', { defaultValue: 'Write a reply...' })}
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{ px: 1.5, py: 0.65 }}
                      />
                      <Box sx={{ px: 1.5, pb: 0.5 }}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'stretch', sm: 'center' }}
                          sx={{
                            p: 0.55,
                            borderRadius: 2,
                            border: '1px dashed',
                            borderColor: attachments.length ? 'primary.main' : 'divider',
                            bgcolor: attachments.length
                              ? alpha(theme.palette.primary.main, 0.06)
                              : alpha(theme.palette.grey[500], 0.04),
                          }}
                        >
                          <Button
                            component="label"
                            size="small"
                            variant="outlined"
                            startIcon={<AttachFileIcon />}
                            disabled={attachments.length >= 5}
                            sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, borderRadius: 2 }}
                          >
                            {translate('tickets_page.attach_files', { defaultValue: 'Attach files' })}
                            <Box component="input" type="file" multiple hidden onChange={handleAttachmentChange} />
                          </Button>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: 1, minWidth: 0 }}>
                            {attachments.length ? (
                              attachments.map((attachment, index) => (
                                <Chip
                                  key={`${attachment.name}-${index}`}
                                  icon={<AttachFileIcon />}
                                  label={`${attachment.name} (${formatFileSize(attachment.size)})`}
                                  onDelete={() => removeAttachment(index)}
                                  deleteIcon={<DeleteOutlineIcon />}
                                  size="small"
                                  variant="outlined"
                                  sx={{ maxWidth: 260 }}
                                />
                              ))
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                {translate('tickets_page.no_attachments_selected', {
                                  defaultValue: 'No attachments selected. Max 5 files, 10 MB each.',
                                })}
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      </Box>
                      <Divider />
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ px: 1, py: 0.45 }}
                      >
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <AttachFileIcon fontSize="small" color={attachments.length ? 'primary' : 'disabled'} />
                          <Typography variant="caption" color="text.secondary">
                            {attachmentReadyLabel}
                          </Typography>
                        </Stack>
                        <Button
                          variant="contained"
                          endIcon={
                            sending ? (
                              <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} />
                            ) : (
                              <SendIcon />
                            )
                          }
                          disabled={sending || !reply.trim()}
                          onClick={sendReply}
                          sx={{ minWidth: 104, borderRadius: 2 }}
                        >
                          {sending
                            ? translate('tickets_page.sending', { defaultValue: 'Sending' })
                            : translate('tickets_page.send', { defaultValue: 'Send' })}
                        </Button>
                      </Stack>
                    </Paper>
                  </Box>
                </>
              ) : (
                <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ height: '100%' }}>
                  <ConfirmationNumberIcon color="disabled" />
                  <Typography color="text.secondary">
                    {translate('tickets_page.select_ticket', { defaultValue: 'Select a ticket to view details.' })}
                  </Typography>
                </Stack>
              )}
            </Box>

            <Box
              sx={{
                borderLeft: { lg: '1px solid' },
                borderColor: 'divider',
                display: { xs: 'none', lg: 'block' },
                bgcolor: alpha(theme.palette.primary.main, 0.025),
                overflow: 'auto',
              }}
            >
              <Stack spacing={2.25} sx={{ p: 2.25 }}>
                <Typography variant="subtitle1" fontWeight={850}>
                  {translate('tickets_page.details', { defaultValue: 'Details' })}
                </Typography>
                {selectedTicket ? (
                  <>
                    <Stack alignItems="center" spacing={1.25} sx={{ py: 1 }}>
                      <TicketRequesterAvatar
                        ticket={selectedTicket}
                        name={requesterName(selectedTicket)}
                        size={64}
                        sx={{ fontSize: 28 }}
                      />
                      <Box sx={{ textAlign: 'center', minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={850} noWrap>
                          {requesterName(selectedTicket)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {selectedTicket.requester_email ||
                            translate('tickets_page.no_email', { defaultValue: 'No email' })}
                        </Typography>
                      </Box>
                    </Stack>
                    <Divider />
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonOutlineIcon fontSize="small" />
                        <Typography variant="subtitle2" fontWeight={800}>
                          {translate('tickets_page.about', { defaultValue: 'About' })}
                        </Typography>
                      </Stack>
                      <DetailRow
                        icon={<AlternateEmailIcon fontSize="small" />}
                        label={translate('tickets_page.email', { defaultValue: 'Email' })}
                        value={selectedTicket.requester_email || '-'}
                      />
                      {(selectedTicket.mailbox_address || mailbox?.email_address) &&
                        !isInternalMailboxAddress(selectedTicket.mailbox_address || mailbox?.email_address) && (
                          <DetailRow
                            icon={<MailOutlineIcon fontSize="small" />}
                            label={translate('tickets_page.mailbox', { defaultValue: 'Mailbox' })}
                            value={selectedTicket.mailbox_address || mailbox?.email_address || '-'}
                          />
                        )}
                      <DetailRow
                        icon={<AccessTimeIcon fontSize="small" />}
                        label={translate('tickets_page.last_update', { defaultValue: 'Last update' })}
                        value={formatDate(selectedTicket.updated_at) || '-'}
                      />
                    </Stack>
                    <Divider />
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <InfoOutlinedIcon fontSize="small" />
                        <Typography variant="subtitle2" fontWeight={800}>
                          {translate('tickets_page.ticket', { defaultValue: 'Ticket' })}
                        </Typography>
                      </Stack>
                      <DetailRow
                        label={translate('tickets_page.display_number', { defaultValue: 'Display number' })}
                        value={displayTicketNo(selectedTicket)}
                      />
                      <DetailRow
                        label={translate('tickets_page.email_thread_id', { defaultValue: 'Email thread ID' })}
                        value={threadTicketNo(selectedTicket) || '-'}
                      />
                      <DetailRow
                        label={translate('tickets_page.conversation', { defaultValue: 'Conversation' })}
                        value={selectedConversationID ? `#${selectedConversationID}` : '-'}
                      />
                      <DetailRow
                        label={translate('tickets_page.messages', { defaultValue: 'Messages' })}
                        value={messageCount}
                      />
                      <DetailRow
                        label={translate('tickets_page.created', { defaultValue: 'Created' })}
                        value={formatDate(selectedTicket.created_at) || '-'}
                      />
                    </Stack>
                    <Divider />
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocalOfferOutlinedIcon fontSize="small" />
                        <Typography variant="subtitle2" fontWeight={800}>
                          {translate('tickets_page.tags', { defaultValue: 'Tags' })}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip
                          label={translateTicketStatus(selectedTicket.status)}
                          color={statusColor(selectedTicket.status)}
                          size="small"
                        />
                        <Chip
                          label={
                            selectedConversationID
                              ? translate('tickets_page.live_chat_tag', { defaultValue: 'live-chat' })
                              : translate('tickets_page.email_tag', { defaultValue: 'email' })
                          }
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    </Stack>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {translate('tickets_page.choose_ticket_details', {
                      defaultValue: 'Choose a ticket to see customer and ticket details.',
                    })}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>
        </Paper>
        </Box>
      )}
      <Dialog open={manualTicketOpen} onClose={closeManualTicketDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>
          {translate('tickets_page.create_ticket', { defaultValue: 'Create Ticket' })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={translate('tickets_page.requester_name', { defaultValue: 'Requester name' })}
              value={manualTicket.requester_name}
              onChange={(event) => updateManualTicket('requester_name', event.target.value)}
              fullWidth
            />
            <TextField
              label={translate('tickets_page.requester_email', { defaultValue: 'Requester email' })}
              type="email"
              value={manualTicket.requester_email}
              onChange={(event) => updateManualTicket('requester_email', event.target.value)}
              fullWidth
              required
            />
            <TextField
              label={translate('tickets_page.subject', { defaultValue: 'Subject' })}
              value={manualTicket.subject}
              onChange={(event) => updateManualTicket('subject', event.target.value)}
              fullWidth
              required
            />
            <TextField
              label={translate('tickets_page.message', { defaultValue: 'Message' })}
              value={manualTicket.message}
              onChange={(event) => updateManualTicket('message', event.target.value)}
              fullWidth
              required
              multiline
              minRows={4}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeManualTicketDialog} disabled={manualTicketSaving}>
            {translate('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="contained" onClick={createManualTicket} disabled={manualTicketSaving}>
            {manualTicketSaving ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              translate('tickets_page.save_ticket', { defaultValue: 'Save Ticket' })
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={Boolean(statusConfirmAction)}
        onClose={() => !statusLoader && setStatusConfirmAction(null)}
        onConfirm={confirmTicketStatusChange}
        loading={statusLoader}
        title={
          statusConfirmAction === 'reopen'
            ? translate('tickets_page.reopen_ticket', { defaultValue: 'Reopen ticket' })
            : translate('tickets_page.close_ticket', { defaultValue: 'Close ticket' })
        }
        message={
          statusConfirmAction === 'reopen'
            ? translate('tickets_page.reopen_ticket_confirm', {
                defaultValue: 'Are you sure you want to reopen this ticket?',
              })
            : translate('tickets_page.close_ticket_confirm', {
                defaultValue: 'Are you sure you want to close this ticket?',
              })
        }
        submitButtonName={
          statusConfirmAction === 'reopen'
            ? translate('tickets_page.reopen', { defaultValue: 'Reopen' })
            : translate('tickets_page.close', { defaultValue: 'Close' })
        }
      />
    </>
  );
}

function MobileTicketsView({
  translate,
  mailbox,
  status,
  handleStatusChange,
  searchText,
  setSearchText,
  fetchTickets,
  filteredTickets,
  loader,
  loadingMoreTickets,
  handleInboxListScroll,
  handleTicketSelect,
  inboxCountLabel,
  inboxCount,
  selectedTicket,
  selectedId,
  messages,
  detailLoader,
  translateTicketStatus,
  statusLoader,
  setStatusConfirmAction,
  reply,
  setReply,
  ccOpen,
  setCcOpen,
  ccValue,
  setCcValue,
  attachments,
  handleAttachmentChange,
  removeAttachment,
  attachmentReadyLabel,
  sending,
  sendReply,
  linkifyText,
  ticketSetupDone,
  openCreateTicketDialog,
  clearSelectedTicket,
}) {
  const theme = useTheme();
  const selectedOpen = selectedTicket?.status === 'open';
  const mobileComposerReserve = selectedOpen
    ? 'calc(176px + 72px + env(safe-area-inset-bottom))'
    : 'calc(72px + env(safe-area-inset-bottom))';

  if (!selectedTicket) {
    return (
      <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: '#f8fafc' }}>
        <Box sx={{ px: 1.25, pt: 1, pb: 0.75, flexShrink: 0 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 1.25,
              borderRadius: 2.5,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(
                theme.palette.background.paper,
                0.98
              )})`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.1}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main' }}>
                <ConfirmationNumberIcon fontSize="small" />
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={900} noWrap>
                    {translate('tickets_page.title', { defaultValue: 'Tickets' })}
                  </Typography>
                  <Chip label={inboxCountLabel} size="small" sx={{ height: 22, fontWeight: 800 }} />
                </Stack>
                {mailbox?.email_address && !isInternalMailboxAddress(mailbox.email_address) && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {mailbox.email_address}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center">
                {ticketSetupDone && (
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={openCreateTicketDialog}
                    aria-label={translate('tickets_page.create_ticket', { defaultValue: 'Create Ticket' })}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton size="small" onClick={() => fetchTickets(status)}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Paper>
        </Box>

        <Box sx={{ px: 1.25, pb: 1, flexShrink: 0 }}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={status}
            onChange={handleStatusChange}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              mb: 1,
              '& .MuiToggleButton-root': {
                border: 0,
                py: 0.9,
                fontWeight: 850,
                textTransform: 'none',
                '&.Mui-selected': {
                  color: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                },
              },
            }}
          >
            <ToggleButton value="all">{translate('tickets_page.all', { defaultValue: 'All' })}</ToggleButton>
            <ToggleButton value="open">{translate('tickets_page.open', { defaultValue: 'Open' })}</ToggleButton>
            <ToggleButton value="closed">{translate('tickets_page.closed', { defaultValue: 'Closed' })}</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            fullWidth
            placeholder={translate('tickets_page.search_tickets', { defaultValue: 'Search tickets...' })}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
                borderRadius: 2,
              },
            }}
          />
        </Box>

        {loader && <LinearProgress />}
        <List
          disablePadding
          onScroll={handleInboxListScroll}
          sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', px: 1.25, pb: 1.25 }}
        >
          {filteredTickets.map((ticket) => {
            const name = requesterName(ticket);
            const unread = Number(ticket.unread_count || 0);
            return (
              <Paper
                key={ticket.id}
                variant="outlined"
                component={ListItemButton}
                onClick={() => handleTicketSelect(ticket)}
                sx={{
                  mb: 1,
                  p: 1.25,
                  borderRadius: 2.5,
                  bgcolor: ticket.id === selectedId ? alpha(theme.palette.primary.main, 0.1) : 'background.paper',
                  borderColor: ticket.id === selectedId ? 'primary.main' : 'divider',
                  alignItems: 'flex-start',
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                <TicketRequesterAvatar ticket={ticket} name={name} size={42} sx={{ mr: 1.25, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.35, minWidth: 0, overflow: 'hidden' }}>
                    <Chip label={displayTicketNo(ticket)} color="primary" size="small" sx={{ height: 22, fontWeight: 900, flexShrink: 0 }} />
                    <Chip label={translateTicketStatus(ticket.status)} color={statusColor(ticket.status)} size="small" sx={{ height: 22, flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', flexShrink: 0 }}>
                      {formatTime(ticket.updated_at || ticket.last_message_at)}
                    </Typography>
                  </Stack>
                  <Typography variant="subtitle2" fontWeight={900} sx={oneLineEllipsisSx}>
                    {ticket.subject || translate('tickets_page.support_ticket', { defaultValue: 'Support ticket' })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ...oneLineEllipsisSx, display: 'block' }}>
                    {ticket.requester_email || translate('tickets_page.no_email', { defaultValue: 'No email' })}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.4 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ ...oneLineEllipsisSx, flex: 1 }}>
                      {cleanMessageContent(ticket.last_message) ||
                        translate('tickets_page.no_message_preview', { defaultValue: 'No message preview' })}
                    </Typography>
                    {unread > 0 && <Badge badgeContent={unread} color="error" sx={{ pr: 1.5 }} />}
                  </Stack>
                </Box>
              </Paper>
            );
          })}
          {loadingMoreTickets && (
            <Stack alignItems="center" sx={{ py: 1.5 }}>
              <CircularProgress size={18} />
            </Stack>
          )}
          {!loader && !inboxCount && (
            <Stack alignItems="center" spacing={1} sx={{ py: 8, px: 2 }}>
              <ConfirmationNumberIcon color="disabled" />
              <Typography variant="body2" color="text.secondary">
                {translate('tickets_page.no_tickets_found', { defaultValue: 'No tickets found.' })}
              </Typography>
            </Stack>
          )}
        </List>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f8fafc',
      }}
    >
      <Paper
        square
        elevation={0}
        sx={{
          px: 1.25,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton size="small" onClick={clearSelectedTicket}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.35 }}>
              <Chip label={displayTicketNo(selectedTicket)} color="primary" size="small" sx={{ height: 22, fontWeight: 900 }} />
              {threadTicketNo(selectedTicket) && (
                <Chip label={threadTicketNo(selectedTicket)} size="small" variant="outlined" sx={{ height: 22 }} />
              )}
              <Chip label={translateTicketStatus(selectedTicket.status)} color={statusColor(selectedTicket.status)} size="small" sx={{ height: 22 }} />
            </Stack>
            <Typography variant="subtitle1" fontWeight={900} noWrap>
              {selectedTicket.subject || translate('tickets_page.support_ticket', { defaultValue: 'Support ticket' })}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {requesterName(selectedTicket)} &lt;
              {selectedTicket.requester_email || translate('tickets_page.no_email', { defaultValue: 'No email' })}&gt;
            </Typography>
          </Box>
          <Button
            size="small"
            variant={selectedOpen ? 'outlined' : 'contained'}
            color={selectedOpen ? 'inherit' : 'primary'}
            disabled={statusLoader}
            onClick={() => setStatusConfirmAction(selectedOpen ? 'close' : 'reopen')}
            sx={{ flexShrink: 0, borderRadius: 2 }}
          >
            {selectedOpen
              ? translate('tickets_page.close', { defaultValue: 'Close' })
              : translate('tickets_page.reopen', { defaultValue: 'Reopen' })}
          </Button>
        </Stack>
      </Paper>

      {detailLoader && <LinearProgress />}
      <Stack
        spacing={1}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          p: 1.25,
          pb: mobileComposerReserve,
        }}
      >
        {messages.map((message) => {
          const isAgent = message.sender_type === 'agent';
          const author = safeText(message.sender_name) || safeText(message.sender_email) || message.sender_type;
          const body = readableTicketMessage(message);
          return (
            <Box key={message.id} sx={{ display: 'flex', justifyContent: isAgent ? 'flex-end' : 'flex-start' }}>
              <Box
                sx={{
                  maxWidth: '88%',
                  p: 1,
                  borderRadius: 2.25,
                  bgcolor: isAgent ? 'primary.main' : 'background.paper',
                  color: isAgent ? 'primary.contrastText' : 'text.primary',
                  border: isAgent ? 'none' : '1px solid',
                  borderColor: 'divider',
                  boxShadow: isAgent ? `0 10px 24px ${alpha(theme.palette.primary.main, 0.18)}` : 'none',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.35 }}>
                  <Typography variant="caption" fontWeight={900}>
                    {author}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.74 }}>
                    {formatDate(message.created_at)}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.65, wordBreak: 'break-word' }}>
                  {body
                    ? linkifyText(body)
                    : translate('tickets_page.message_received', { defaultValue: 'Message received.' })}
                </Typography>
                {message.attachments?.length > 0 && (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
                    {message.attachments.map((attachment) => (
                      <Chip
                        key={attachment.id || attachment.url}
                        component="a"
                        href={attachment.url}
                        target="_blank"
                        rel="noreferrer"
                        clickable
                        icon={<AttachFileIcon />}
                        label={attachment.name || translate('tickets_page.attachment', { defaultValue: 'Attachment' })}
                        size="small"
                        sx={{
                          maxWidth: 220,
                          bgcolor: isAgent ? alpha(theme.palette.common.white, 0.16) : alpha(theme.palette.primary.main, 0.08),
                          color: isAgent ? 'primary.contrastText' : 'text.primary',
                          '& .MuiChip-icon': { color: isAgent ? 'primary.contrastText' : 'primary.main' },
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>
          );
        })}
        {!messages.length && (
          <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ minHeight: 220 }}>
            <MailOutlineIcon color="disabled" />
            <Typography color="text.secondary">
              {translate('tickets_page.no_messages_yet', { defaultValue: 'No messages yet.' })}
            </Typography>
          </Stack>
        )}
      </Stack>

      {selectedOpen && (
        <Paper
          square
          elevation={0}
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 'calc(72px + env(safe-area-inset-bottom))',
            zIndex: 1199,
            p: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {translate('tickets_page.to', { defaultValue: 'To' })}:
            </Typography>
            <Typography variant="caption" fontWeight={800} noWrap sx={{ flex: 1 }}>
              {selectedTicket.requester_email || translate('tickets_page.no_email', { defaultValue: 'No email' })}
            </Typography>
            <Button size="small" variant={ccOpen ? 'contained' : 'outlined'} onClick={() => setCcOpen((value) => !value)} sx={{ minWidth: 46, borderRadius: 4 }}>
              CC
            </Button>
          </Stack>
          {ccOpen && (
            <TextField
              fullWidth
              size="small"
              value={ccValue}
              onChange={(event) => setCcValue(event.target.value)}
              placeholder={translate('tickets_page.add_cc_placeholder', {
                defaultValue: 'Add CC emails separated by comma',
              })}
              sx={{ mb: 0.75 }}
            />
          )}
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            value={reply}
            onChange={(event) => setReply(event.target.value)}
            placeholder={translate('tickets_page.write_reply', { defaultValue: 'Write a reply...' })}
            sx={{
              mb: 0.75,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: alpha(theme.palette.grey[500], 0.04),
              },
            }}
          />
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Button
              component="label"
              size="small"
              variant="outlined"
              disabled={attachments.length >= 5}
              sx={{ minWidth: 42, px: 1, borderRadius: 2 }}
            >
              <AttachFileIcon fontSize="small" />
              <Box component="input" type="file" multiple hidden onChange={handleAttachmentChange} />
            </Button>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
              {attachmentReadyLabel}
            </Typography>
            <Button
              variant="contained"
              disabled={sending || (!reply.trim() && !attachments.length)}
              onClick={sendReply}
              endIcon={sending ? <CircularProgress size={15} thickness={5} sx={{ color: 'inherit' }} /> : <SendIcon />}
              sx={{ minWidth: 92, borderRadius: 2 }}
            >
              {sending
                ? translate('tickets_page.sending', { defaultValue: 'Sending' })
                : translate('tickets_page.send', { defaultValue: 'Send' })}
            </Button>
          </Stack>
          {attachments.length > 0 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
              {attachments.map((attachment, index) => (
                <Chip
                  key={`${attachment.name}-${index}`}
                  icon={<AttachFileIcon />}
                  label={attachment.name}
                  onDelete={() => removeAttachment(index)}
                  deleteIcon={<DeleteOutlineIcon />}
                  size="small"
                  variant="outlined"
                  sx={{ maxWidth: 160 }}
                />
              ))}
            </Stack>
          )}
        </Paper>
      )}
    </Box>
  );
}

function DetailRow({ icon = null, label, value }) {
  const displayValue = value === undefined || value === null || value === '' ? '-' : value;
  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="flex-start"
      justifyContent="space-between"
      sx={{ minWidth: 0, width: '100%' }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flexShrink: 0 }}>
        {icon}
        <Typography variant="body2" color="text.secondary" noWrap>
          {label}
        </Typography>
      </Stack>
      <Typography
        variant="body2"
        fontWeight={700}
        textAlign="right"
        sx={{
          minWidth: 0,
          flex: 1,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        {displayValue}
      </Typography>
    </Stack>
  );
}
